import React from "react";
import {
  BarChart,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ComposedChart,
  Bar,
  Scatter,
  LabelList,
  ReferenceLine,
  Brush
} from "recharts";

import DataTable from "./DataTable";


/* -------------------------
  Helper: tooltip formatting
  ------------------------- */

const fmtDollar = (v) =>
  `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  
const SERIES_COLORS = {
  cy: "#002868", // blue bar
  hist: "#e11d48", // red dot
};

const STACKED_COLORS = [
  "#003f5c", // deep navy blue
  "#2f4b7c", // slate blue
  "#376a7f", // desaturated blue-green
  "#468f85", // soft teal
  "#56b08a", // muted sea green
  "#74c69d", // sage green
  "#9ec5ab", // misty green-gray
  "#b3c2b9", // cool gray-green
  "#cfd6d1", // light warm gray
  "#000000", // black
  "#7a9ba6", // smoky steel blue
  "#5f7e88", // darker steel gray
];


// Custom tooltip that shows only the hovered Bar segment
function CustomStackTooltip({ active, payload, label, hoveredKey,fmt }) {
  if (!active || !payload || payload.length === 0) return null;

  // find the hovered series; fallback to the last one (Recharts behavior)
  const item =
    (hoveredKey && payload.find((p) => p.dataKey === hoveredKey)) ||
    payload[payload.length - 1];

  if (!item) return null;
  return (
    <div className="recharts-default-tooltip">
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            background: item.color,
            borderRadius: 2,
          }}
        />
        <span style={{ color: "#334155" }}>
          {item.name || item.dataKey} : {fmt(item.value)}
        </span>
      </div>
    </div>
  );
}

function CustomPercentStackTooltip({ active, payload, label, hoveredKey }) {
  if (!active || !payload || payload.length === 0) return null;

  const item =
    (hoveredKey && payload.find((p) => p.dataKey === hoveredKey)) ||
    payload[payload.length - 1];

  if (!item) return null;
  return (
    <div className="recharts-default-tooltip">
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            background: item.color,
            borderRadius: 2,
          }}
        />
        <span style={{ color: "#334155" }}>
          {item.name || item.dataKey} : {twoDec(item.value)}%
        </span>
      </div>
    </div>
  );
}

//Custom tooltip for combo chart
function CustomComboTooltip({ active, label, payload }) {
  if (!active || !payload || payload.length === 0) return null;

  // only show the actual plotted series
  const rows = payload.filter(
    (p) => p.dataKey === "cy" || p.dataKey === "hist"
  );

  return (
    <div className="recharts-default-tooltip">
      {/* top label = month */}
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>

      {rows.map((p) => (
        <div
          key={p.dataKey}
          style={{ display: "flex", gap: 6, alignItems: "center" }}
        >
			<span
			  style={{
				display: "inline-block",
				width: p.dataKey === "hist" ? 8 : 10,
				height: p.dataKey === "hist" ? 8 : 10,
				background: SERIES_COLORS[p.dataKey] || p.color,
				borderRadius: p.dataKey === "hist" ? "50%" : 2,
			  }}
			/>


          <span style={{ color: "#334155" }}>
            {p.dataKey === "cy" ? "Current Year" : "Historical"}:{" "}
            {twoDec(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}



/* -------------------------
  Helper: default tooltip formatter
  ------------------------- */
const twoDec = (v) => (v == null || v === "" ? "" : Number(v).toFixed(2));

/* --------------------------
  Helper: number formatting 
   ------------------------ */

const fmtCompactNumber = (n) => {
  const v = Number(n) || 0;
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);

  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000)     return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)         return `${sign}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${abs.toFixed(0)}`;
};


/* ---------------------------
	Stacked bar Totals Helper
	----------------------- */
const fmtCompactDollars = (n) => {
  const v = Number(n) || 0;
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);

  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000)     return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)         return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};


/* -------------------------
	Bar Helper function
	-----------------------*/
const TransparentBarShape = (props) => (
	<rect {...props} fill="transparent" pointerEvents="none" />
);



/* -------------------------
  TimeSeriesChart
  Props:
    - data: [{date: "yyyy-mm-dd", value: 123}, ...]
    - metadata: { title, y_axis_label, ... }
  ------------------------- */
function TimeSeriesChart({ data, metadata }) {
  /* track the Brush window so the y-axis rescales to what's visible */
  const [brush, setBrush] = React.useState(null);

  React.useEffect(() => { setBrush(null); }, [data]);

  /* pad 10% of the visible range below the low point, 5% above the high point */
  const yDomain = React.useMemo(() => {
    const start = brush?.startIndex ?? 0;
    const end = brush?.endIndex ?? data.length - 1;

    let min = Infinity;
    let max = -Infinity;
    for (let i = start; i <= end && i < data.length; i++) {
      const v = Number(data[i]?.value);
      if (!Number.isFinite(v)) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    if (min === Infinity) return [0, "auto"];

    /* flat line: fall back to a span derived from the value itself */
    const span = (max - min) || Math.abs(max) || 1;
    return [min - span * 0.1, max + span * 0.05];
  }, [data, brush]);

  return(
	<div className="chart-row">
	  {/* LEFT: external rotated Y label (stays fixed) */}
	  <div className="y-axis-outer-label">
		<div className="y-axis-rotated">
		  {metadata?.y_label || "Value"}
		</div>
	  </div>

	  {/* RIGHT: chart area */}
	  <div className="chart-container">
		{data.length === 0 ? (
		  <div className="chart-placeholder">Choose filters to load a dataset</div>
		) : (
			<div className="chart-inner" style={{ width: "100%", height: "460px" }}>  
			  <ResponsiveContainer width="100%" height="100%">
				<LineChart
				  data={data}
				  /* reserve some left space inside the chart for the tick labels */
				  margin={{ top: 22, right: 60, left: 20, bottom: 50 }}
				>
				  <XAxis
					dataKey="date"
					tickFormatter={(str) =>
					  new Date(str).toLocaleDateString("en-US", { month: "short", year: "2-digit" })
					}
					/*interval="preserveStartEnd"*/
				  />
				  {/* width reserves inner left space for tick labels so axis moves right */}
				  <YAxis
					width={75}
					domain={yDomain}
					tickFormatter={(v) =>
					  Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })
					}
				  />
				  <Tooltip 
				  formatter={(value) => Number(value).toFixed(2)}
				  />
				  <Line type="monotone" dataKey="value" stroke="#002868" strokeWidth={2} dot={false} />
				  <Brush
                    dataKey="date"
                    height={30}
                    stroke="#002868"
					onChange={setBrush}
					travellerWidth={8}
					style={{ fontSize: "10px" }}
                    tickFormatter={(str) =>
                      new Date(str).toLocaleDateString("en-US", { month: "short", year: "2-digit" })
                    }
                  />
				</LineChart>
			  </ResponsiveContainer>
			</div>  
		)}
	  </div>
	</div>
  );
}


function ComboCyHist({ data, metadata }) {
  console.log("Metadata received:", metadata);
  if (!data || data.length === 0) {
    console.warn("ComboCyHist: No data passed in!");
  }
  return (
    <div style={{ width: "100%", height: "460px" }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 40, left: 16, bottom: 8 }}>
          <XAxis dataKey="month" />
          <YAxis
            width={70}
            label={{
              value: metadata?.y_axis_label || "Value",
              angle: -90,
              position: "insideLeft",
              style: { textAnchor: "middle" },
            }}
          />
		  <Tooltip content={<CustomComboTooltip />} />
          <Legend verticalAlign="bottom" />
          <Bar dataKey="cy" name="Current Year" barSize={36} fill="#002868" />
          <Scatter dataKey="hist" name="Historical" fill="#e11d48" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// === New Stacked Bar Component ===
function StackedBarChart({ data, metadata }) {
  const [hoveredKey, setHoveredKey] = React.useState(null);
  
  if (!data || data.length === 0) {
    return (
      <div className="chart-row">
        <div className="y-axis-outer-label">
          <div className="y-axis-rotated">
            {metadata?.y_label || "Value"}
          </div>
        </div>
        <div className="chart-container">
          <div className="chart-placeholder">No data available</div>
        </div>
      </div>
    );
  }

  const xLabel = metadata?.x_label || "X-Axis";
  const yLabel = metadata?.y_label || "Value";
  const valueFormat = (metadata?.value_format || "currency").toLowerCase();
  const fmtCompact = valueFormat === "number" ? fmtCompactNumber : fmtCompactDollars;


  const groupedData = Object.values(
    data.reduce((acc, row) => {
      const x = row.x_axis;
      const s = row.series;
      const v = parseFloat(row.value);
      if (!acc[x]) acc[x] = { x_axis: x };
      acc[x][s] = (acc[x][s] || 0) + v;
      return acc;
    }, {})
  );
  
  console.log("example grouped row:", groupedData[0]);
  const stackKeys = [...new Set(data.map((d) => d.series))];
  
  const groupedWithTotals = groupedData.map((row) => {
	  const total = stackKeys.reduce((sum, k) => sum + (Number(row[k]) || 0), 0);
	  return { ...row, __total: total };
  });
  
	const rowsWithTopKey = groupedWithTotals.map((row) => {
	  // total of positive stack ONLY (so negatives can dip below axis without affecting label placement)
	  const posTotal = stackKeys.reduce((sum, k) => {
		const v = Number(row[k]) || 0;
		return sum + (v > 0 ? v : 0);
	  }, 0);

	  // top key among POSITIVE segments only
	  const topPosKey =
		[...stackKeys].reverse().find((k) => (Number(row[k]) || 0) > 0) || null;

	  return { ...row, __posTotal: posTotal, __topPosKey: topPosKey };
	});


  
  return (
    <div className="chart-row">
      {/* LEFT: rotated y-axis label */}
      <div className="y-axis-outer-label">
        <div className="y-axis-rotated">{yLabel}</div>
      </div>

      {/* RIGHT: chart area */}
      <div className="chart-container">
        <div className="chart-inner">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rowsWithTopKey}
			  stackOffset="sign"
              margin={{ top: 22, right: 40, left: 10, bottom: 10 }}
            >
			  <ReferenceLine
				  y={0}
				  stroke="#9ca3af"
				  strokeWidth={1}
				  strokeDasharray="3 3"
				/>

              <XAxis
                dataKey="x_axis"
                label={{  position: "bottom", dy: 20 }}
              />
              <YAxis 
				width={75}
				domain={[(min) => Math.min(0, min), (max) => Math.max(0, max)]}

				tickFormatter={(value) => fmtCompact(value)}
				/>
			   <Tooltip
			    formatter={(val) => fmtCompact(val)}
			    content={<CustomStackTooltip hoveredKey={hoveredKey} fmt={fmtCompact} />}
			    wrapperStyle={{ outline: "none" }}
			  />

              <Legend 
				verticalAlign="bottom"
				margin={{bottom: 10}}
			  />
				{stackKeys.map((key, idx) => (
				  <Bar
					key={key}
					dataKey={key}
					name={key}
					stackId="a"
					fill={STACKED_COLORS[idx % STACKED_COLORS.length]}
					barSize={60}
					onMouseEnter={() => setHoveredKey(key)}
					onMouseLeave={() => setHoveredKey(null)}
				  >
					{/* Put the total label ONLY on the top-most segment */}
					{(
					<LabelList
					  content={({ index, viewBox }) => {
						const row = rowsWithTopKey?.[index];
						if (!row || !viewBox) return null;

						// Only draw the total on the bar segment that is top for this x_axis
						if (row.__topPosKey !== key) return null;

						const total = Number(row.__total) || 0;
						if (!Number.isFinite(total) || total === 0) return null;

						return (
						  <text
							x={viewBox.x + viewBox.width / 2}
							y={viewBox.y - 10}
							textAnchor="middle"
							fontSize={12}
							fontWeight={700}
							fill="#0f172a"
							pointerEvents="none"
						  >
							{fmtCompact(total)}
						  </text>
						);
					  }}
					/>
					)}
				  </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function PercentBarWithTotal({ data, metadata }) {
  const [hoveredKey, setHoveredKey] = React.useState(null);

  if (!data || data.length === 0) {
    return (
      <div className="chart-row">
        <div className="y-axis-outer-label">
          <div className="y-axis-rotated">
            {metadata?.y_label || "Percent"}
          </div>
        </div>
        <div className="chart-container">
          <div className="chart-placeholder">No data available</div>
        </div>
      </div>
    );
  }

  const yLabel = metadata?.y_label || "Percent";

  const categoryRows = data.filter((row) => row.row_type === "category");
  const totalsByCharacteristic = data.reduce((acc, row) => {
    if (row.row_type !== "total") return acc;
    acc[row.characteristic] = Number(row.value);
    return acc;
  }, {});

  const groupedData = Object.values(
    categoryRows.reduce((acc, row) => {
      const x = row.characteristic;
      const s = row.label_group_clean;
      const v = Number(row.value);
      if (!acc[x]) acc[x] = { x_axis: x };
      acc[x][s] = (acc[x][s] || 0) + v;
      return acc;
    }, {})
  );

  const stackKeys = [...new Set(categoryRows.map((d) => d.label_group_clean))];

  const groupedWithTotals = groupedData.map((row) => {
    const total = totalsByCharacteristic[row.x_axis];
    return { ...row, __total: total };
  });

  const rowsWithTopKey = groupedWithTotals.map((row) => {
    const topPosKey =
      [...stackKeys].reverse().find((k) => (Number(row[k]) || 0) > 0) || null;
    return { ...row, __topPosKey: topPosKey };
  });

  const renderWrappedTick = ({ x, y, payload }) => {
    const text = String(payload.value || "");
    const words = text.split(/\s+/).filter(Boolean);
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
      const nextLine = currentLine ? `${currentLine} ${word}` : word;
      if (nextLine.length > 20 && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = nextLine;
      }
    });

    if (currentLine) lines.push(currentLine);

    return (
      <text x={x} y={y + 12} textAnchor="middle" fill="#475569">
        {lines.map((line, index) => (
          <tspan key={line} x={x} dy={index === 0 ? 0 : 14}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  return (
    <div className="chart-row">
      <div className="y-axis-outer-label">
        <div className="y-axis-rotated">{yLabel}</div>
      </div>

      <div className="chart-container">
        <div className="chart-inner">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rowsWithTopKey} margin={{ top: 22, right: 40, left: 10, bottom: 10 }}>
              <XAxis
                dataKey="x_axis"
                label={{ position: "bottom", dy: 20 }}
                interval={0}
                tick={renderWrappedTick}
                height={80}
              />
              <YAxis
                width={75}
                domain={[0, 100]}
                tickFormatter={(value) => `${Math.round(value)}%`}
              />
              <Tooltip
                formatter={(val) => `${twoDec(val)}%`}
                content={<CustomPercentStackTooltip hoveredKey={hoveredKey} />}
                wrapperStyle={{ outline: "none" }}
              />
              <Legend verticalAlign="bottom" margin={{ bottom: 10 }} />
              {stackKeys.map((key, idx) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={key}
                  stackId="a"
                  fill={STACKED_COLORS[idx % STACKED_COLORS.length]}
                  barSize={60}
                  onMouseEnter={() => setHoveredKey(key)}
                  onMouseLeave={() => setHoveredKey(null)}
                >
                  <LabelList
                    content={({ index, viewBox }) => {
                      const row = rowsWithTopKey?.[index];
                      if (!row || !viewBox) return null;

                      if (row.__topPosKey !== key) return null;

                      const total = Number(row.__total);
                      if (!Number.isFinite(total)) return null;

                      return (
                        <text
                          x={viewBox.x + viewBox.width / 2}
                          y={viewBox.y - 10}
                          textAnchor="middle"
                          fontSize={12}
                          fontWeight={700}
                          fill="#0f172a"
                          pointerEvents="none"
                        >
                          {`Pop. = ${total.toLocaleString()}`}
                        </text>
                      );
                    }}
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

/* -------------------------
  ChartRenderer (dispatcher)
  Props:
    - selected: manifest entry (contains chart_type)
    - data: parsed chart data
    - metadata: dataset metadata
  ------------------------- */

export default function ChartRenderer({ selected, data, metadata }) {
  

  const type = selected?.chart_type || "time_series";

  switch (type) {
    case "combo_cy_hist_marker":
      return <ComboCyHist data={data} metadata={metadata} />;

    // new table types: "table" and "table_rankings"
    case "table":
      // metadata.table_columns (optional) can specify column order/labels.
      // Data should be an array of objects (parsed via Papa.parse header:true).
	  
      return <DataTable data={data} columnsDef={metadata?.table_columns ?? null} />;

    case "time_series":
    default:
      return <TimeSeriesChart data={data} metadata={metadata} />;
	  
	case "stacked_bar":
      return <StackedBarChart data={data} metadata={metadata} />;

	case "percent_bar_w_total":
      return <PercentBarWithTotal data={data} metadata={metadata} />;
  }
}
